// @ts-check
// Árvore do fluxo desenhada da esquerda para a direita (spec 2.0.0, fluxos.md seção 20.5, "Estrutura"; contrato 09 de
// fluxos, seção 17), sem medir nada em JavaScript: cada lista é uma linha flexível com o "+" antes de cada passo e um
// depois do último; a divisão é sempre o último passo da lista e, no lugar do "+", abre a coluna dos caminhos (SIM em
// cima, NÃO embaixo), cada caminho com o rótulo e a própria lista, seguindo para a direita. O colchete e os tocos são
// linhas do CSS (flows.css). Os caminhos nunca se juntam. Os bots (contrato de bots, seção 22.2) usam a mesma árvore com
// de 2 a 12 ramos por bloco: quem usa passa no contexto os ramos, o rótulo, o resumo, o círculo e os números de cada
// bloco, e os blocos terminais ("Ir para", "Encerrar") ficam sem o "+" depois, com o atalho para o destino. Sem essas
// funções, a árvore é a dos fluxos, sem mudança.
import { html } from 'htm/preact';
import { createContext } from 'preact';
import { useContext } from 'preact/hooks';
import { BRANCH_KEYS_BY_TYPE, SPLIT_NODE_TYPES } from '../../../../shared/flows/flow-definition.js';
import { insertPointKey } from '../../lib/flow-editor-state.js';
import { emptyListFocus } from '../../lib/flow-canvas-keyboard.js';
import { FlowConnector, FlowBranchLabel, branchLabelText } from './flow-connector.js';
import { FlowNodeCard } from './flow-node-card.js';
import { describeNode } from './flow-ui.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {import('../../../../shared/flows/flow-definition.js').FlowDefinition} FlowDefinition
 * @typedef {import('../../../../shared/flows/flow-definition.js').FlowNode} FlowNode
 * @typedef {import('../../../../shared/flows/flow-summary.js').FlowSummaryLookups} FlowSummaryLookups
 * @typedef {import('../../../../shared/flows/flow-problems.js').FlowProblem} FlowProblem
 * @typedef {import('../../lib/flow-editor-state.js').FlowInsertPoint} FlowInsertPoint
 * @typedef {import('../../data/flows.js').FlowNodeCounts} FlowNodeCounts
 * @typedef {{
 *   lookups: FlowSummaryLookups, problemsByNode: Map<string, FlowProblem[]>, stepNumbers: Map<string, number>, selectedNodeId: string | null,
 *   focusTarget: string, counts: FlowNodeCounts | null, showCounts: boolean, flowId: string | null, isReadOnly: boolean,
 *   activePointKey: string | null, isDragging: boolean, isPulsing: boolean, period?: { from?: string | null, to?: string | null },
 *   insertDisabledReason: (point: FlowInsertPoint) => string | null,
 *   onInsert?: (point: FlowInsertPoint, element: HTMLElement) => void, onDropAction?: (point: FlowInsertPoint, itemKey: string) => void,
 *   onSelectNode?: (nodeId: string) => void, onDeleteNode?: (nodeId: string) => void,
 *   branchKeysOf?: (node: any) => string[], isSplitNode?: (node: any) => boolean, isTerminalNode?: (node: any) => boolean,
 *   describe?: (node: any) => { summary: any, title: string, text: string }, branchLabel?: (branchKey: string, parentId: string | null) => string,
 *   renderBranchLabel?: (node: any, branchKey: string, position: number) => any, renderCircle?: (node: any) => any,
 *   renderNodeCounts?: (node: any, isFocused: boolean) => any, problemText?: (problem: any) => string,
 *   nodeShortcut?: (node: any) => { label: string, targetId: string | null } | null, onGoToNode?: (nodeId: string) => void,
 *   emptyRootLabel?: string,
 * }} FlowTreeContextValue
 *   branchKeysOf, isSplitNode, isTerminalNode, describe, branchLabel, renderBranchLabel, renderCircle, renderNodeCounts,
 *   problemText, nodeShortcut e emptyRootLabel: a árvore de outra área (os bots); sem elas, as regras dos fluxos
 */

export const FlowTreeContext = createContext(/** @type {FlowTreeContextValue | null} */ (null));

/** @param {string | null | undefined} type */
const isSplit = (type) => Boolean(type && SPLIT_NODE_TYPES.includes(type));

/** O passo abre caminhos? Pela regra de quem usa a árvore, ou pelos tipos dos fluxos. @param {FlowTreeContextValue} context @param {any} node */
const isSplitIn = (context, node) => (context.isSplitNode ? context.isSplitNode(node) : isSplit(node.type));

/** Título e resumo do passo. @param {FlowTreeContextValue} context @param {any} node */
const describeIn = (context, node) => (context.describe ? context.describe(node) : describeNode(node, context.lookups));

/** Texto de um caminho. @param {FlowTreeContextValue} context @param {string} branchKey @param {string | null} parentId */
const branchTextIn = (context, branchKey, parentId) => (context.branchLabel ? context.branchLabel(branchKey, parentId) : branchLabelText(branchKey));

/** Ramos de uma divisão, na ordem do canvas. @param {FlowTreeContextValue} context @param {any} node @returns {string[]} */
export const branchKeysIn = (context, node) => (context.branchKeysOf
  ? context.branchKeysOf(node)
  : [...(BRANCH_KEYS_BY_TYPE[/** @type {keyof typeof BRANCH_KEYS_BY_TYPE} */ (node.type)] ?? [])]);

/**
 * Texto do "+" para o leitor de tela: depois de um passo, no começo de um caminho ou no fim.
 * @param {FlowTreeContextValue} context @param {FlowNode | null} previous @param {string | null} branchKey @param {string | null} parentId @param {boolean} isEnd
 */
function connectorLabel(context, previous, branchKey, parentId, isEnd) {
  if (previous) {
    const name = describeIn(context, previous).title;
    return isEnd ? t('flows.canvas.insertEnd', { name }) : t('flows.canvas.insertAfter', { name });
  }
  if (branchKey) return t('flows.canvas.insertBranchStart', { branch: branchTextIn(context, branchKey, parentId) });
  return t('flows.canvas.insertStart');
}

/**
 * Uma lista de passos (a raiz ou um caminho), com os "+" e, no fim, a coluna dos caminhos da divisão.
 * @param {{ list: FlowNode[], parentId: string | null, branchKey: string | null, depth: number }} props
 */
export function FlowList({ list, parentId, branchKey, depth }) {
  const context = /** @type {FlowTreeContextValue} */ (useContext(FlowTreeContext));
  /** @param {number} index @param {FlowNode | null} previous @param {boolean} isEnd */
  const connector = (index, previous, isEnd) => {
    const point = { parentId, branchKey, index };
    const key = insertPointKey(point);
    return html`<${FlowConnector} key=${`connector-${key}`} point=${point} label=${connectorLabel(context, previous, branchKey, parentId, isEnd)}
      isActive=${context.activePointKey === key} isDragging=${context.isDragging} isPulsing=${context.isPulsing} isEnd=${isEnd}
      disabledReason=${context.isReadOnly ? null : context.insertDisabledReason(point)} isReadOnly=${context.isReadOnly}
      onInsert=${context.onInsert} onDropAction=${context.onDropAction} />`;
  };
  /** @type {any[]} */
  const items = [];
  // o "+" do começo; num caminho vazio ele também é o alvo do teclado
  if (list.length === 0 && parentId) {
    items.push(html`<span key="empty-focus" class="flow-empty-branch" role="treeitem" aria-level=${depth + 1}
      aria-label=${t('flows.canvas.emptyBranch', { branch: branchTextIn(context, branchKey ?? '', parentId) })}
      tabindex=${context.focusTarget === emptyListFocus(parentId, branchKey) ? 0 : -1} data-focus-id=${emptyListFocus(parentId, branchKey)}>
      ${connector(0, null, true)}
    </span>`);
    return html`${items}`;
  }
  if (list.length === 0) {
    items.push(html`<span key="empty-root" class="flow-empty-branch" data-focus-id=${emptyListFocus(null, null)}
      tabindex=${context.focusTarget === emptyListFocus(null, null) ? 0 : -1} role="treeitem" aria-level="1" aria-label=${context.emptyRootLabel ?? t('flows.canvas.emptyFlow')}>
      ${connector(0, null, true)}
    </span>`);
    return html`${items}`;
  }
  items.push(connector(0, null, false));
  list.forEach((node, position) => {
    const described = describeIn(context, node);
    const isFocused = context.focusTarget === node.id;
    items.push(html`<${FlowNodeCard} key=${node.id} node=${node} summary=${described.summary} title=${described.title} summaryText=${described.text}
      problems=${context.problemsByNode.get(node.id) ?? []} stepNumber=${context.stepNumbers.get(node.id) ?? position + 1} level=${depth + 1}
      isSelected=${context.selectedNodeId === node.id} isFocused=${isFocused} isReadOnly=${context.isReadOnly}
      count=${context.counts?.nodes?.[node.id] ?? null} showCounts=${context.showCounts} flowId=${context.flowId} period=${context.period}
      onSelect=${context.onSelectNode} onDelete=${context.onDeleteNode} circle=${context.renderCircle?.(node)} problemTextOf=${context.problemText}
      countsContent=${context.renderNodeCounts ? context.renderNodeCounts(node, isFocused) : undefined} />`);
    if (isSplitIn(context, node)) items.push(html`<${FlowFork} key=${`fork-${node.id}`} node=${node} depth=${depth} />`);
    else if (context.isTerminalNode?.(node)) items.push(html`<${FlowTerminalTail} key=${`tail-${node.id}`} node=${node} />`);
    else items.push(connector(position + 1, node, position === list.length - 1));
  });
  return html`${items}`;
}

/**
 * Coluna dos caminhos de uma divisão, na ordem dos ramos (SIM em cima, NÃO embaixo; ATÉ O DIA em cima).
 * @param {{ node: FlowNode, depth: number }} props
 */
export function FlowFork({ node, depth }) {
  const context = /** @type {FlowTreeContextValue} */ (useContext(FlowTreeContext));
  const keys = branchKeysIn(context, node);
  const branches = context.counts?.nodes?.[node.id]?.branches ?? null;
  return html`<div class="flow-fork" role="presentation">
    ${keys.map((branchKey, position) => html`<div key=${branchKey} class="flow-branch" role="group" aria-label=${t('flows.canvas.branchGroup', { branch: branchTextIn(context, branchKey, node.id) })}
      data-position=${position === 0 ? 'first' : position === keys.length - 1 ? 'last' : 'middle'}>
      ${context.renderBranchLabel
        ? context.renderBranchLabel(node, branchKey, position)
        : html`<${FlowBranchLabel} branchKey=${branchKey} count=${context.showCounts && branches ? branches[branchKey] ?? 0 : null} flowId=${context.flowId}
            splitId=${node.id} position=${position} />`}
      <${FlowList} list=${node.branches?.[branchKey] ?? []} parentId=${node.id} branchKey=${branchKey} depth=${depth + 1} />
    </div>`)}
  </div>`;
}

/**
 * Fim do caminho depois de um passo terminal: nada se insere depois dele; com atalho (o "Ir para" dos bots), o botão
 * com o destino, que leva até ele no canvas.
 * @param {{ node: FlowNode }} props
 */
export function FlowTerminalTail({ node }) {
  const context = /** @type {FlowTreeContextValue} */ (useContext(FlowTreeContext));
  const shortcut = context.nodeShortcut?.(node) ?? null;
  return html`<div class="flow-terminal-tail" aria-hidden=${shortcut ? undefined : 'true'}>
    <span class="flow-terminal-line" aria-hidden="true"></span>
    ${shortcut
      ? html`<button type="button" class="flow-terminal-shortcut" tabindex="-1" disabled=${!shortcut.targetId}
          onClick=${(/** @type {MouseEvent} */ event) => {
            event.stopPropagation();
            if (shortcut.targetId) context.onGoToNode?.(shortcut.targetId);
          }}>${shortcut.label}</button>`
      : html`<span class="flow-terminal-cap"></span>`}
  </div>`;
}
